import { UnifiedListLayout } from "@/components/layout/unified-list-layout";
import { UnifiedSearch } from "@/components/shared/UnifiedSearch";
import { Button } from "@/components/ui/button";
import { useChapters, useCreateChapter } from "@/hooks/useChapters";
import { useDialog } from "@/hooks/useDialog";
import { useProjectMeta } from "@/hooks/meta/useProjectMeta";
import { Download, Plus, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BulkChapterExport } from "./bulk-chapter-export";
import { ChaptersTable } from "./chapters-table";
import { IntroCard } from "@/components/shared/IntroCard";

export function ChaptersOverview() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { currentStoryId, name: projectName } = useProjectMeta(projectId || null);
    const storyId = currentStoryId?.toString();

    const { data: chapters = [], isLoading } = useChapters(storyId, { enabled: !!storyId });
    const createChapterMutation = useCreateChapter();
    const { openDialog } = useDialog();
    const [searchQuery, setSearchQuery] = useState("");
    const [showIntro, setShowIntro] = useState(true);

    useEffect(() => {
        if (projectId) {
            const isClosed = localStorage.getItem(`intro_card_manuscript_${projectId}`);
            if (isClosed === 'true') {
                setShowIntro(false);
            }
        }
    }, [projectId]);

    const dismissIntro = () => {
        if (projectId) {
            localStorage.setItem(`intro_card_manuscript_${projectId}`, 'true');
            setShowIntro(false);
        }
    };

    const handleAddChapter = () => {
        if (!projectId || !storyId) {
            return;
        }

        openDialog('form', {
            title: "Create New Chapter",
            description: "Create a new chapter with description and outline",
            fields: [
                { name: "name", label: "Chapter Name", type: "text", placeholder: "Chapter 1: The Beginning", required: true },
                { name: "description", label: "Description / Outline", type: "textarea", placeholder: "Describe what happens in this chapter..." }
            ],
            submitText: "Create Chapter"
        }, async (data: { name: string; description: string }) => {
            try {
                await createChapterMutation.mutateAsync({
                    storyId: parseInt(storyId),
                    title: data.name,
                    description: data.description,
                    projectId
                });
                toast.success('Chapter created successfully');
            } catch (error) {
                console.error('Error creating chapter:', error);
                toast.error('Failed to create chapter');
            }
        });
    };

    // Filter chapters based on search
    const filteredChapters = chapters.filter(chapter =>
        chapter.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        chapter.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const Toolbar = (
        <>
            <UnifiedSearch
                placeholder="Search chapters..."
                value={searchQuery}
                onChange={setSearchQuery}
                className="w-full max-w-sm"
            />
            <div className="flex items-center gap-2">
                {projectId && (
                    <BulkChapterExport
                        projectId={projectId}
                        projectName={projectName}
                        trigger={
                            <Button variant="outline">
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </Button>
                        }
                    />
                )}
                <Button
                    variant="outline"
                    onClick={() => navigate(`/projects/${projectId}/chapters/plan-scenes`)}
                >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Plan Scenes
                </Button>
                <Button onClick={handleAddChapter}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Chapter
                </Button>
            </div>
        </>
    );

    return (
        <UnifiedListLayout toolbar={Toolbar}>
            <div className="flex flex-col gap-6">
                {showIntro && (
                    <IntroCard
                        title="Architect your scenes"
                        titleLine2="step by step."
                        description="Break your chapters down into actionable beats. This context will be instantly available in the editor, ensuring your prose flows effortlessly from your plan."
                        onClose={dismissIntro}
                        actions={
                            <div className="flex flex-col gap-3">
                                <Button
                                    className="w-full rounded-xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/10 gap-2 h-11"
                                    onClick={() => navigate(`/projects/${projectId}/chapters/plan-scenes`)}
                                >
                                    <Sparkles className="h-4 w-4" />
                                    Build your chapter
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5 transition-all text-muted-foreground hover:text-primary gap-2 h-11"
                                    onClick={handleAddChapter}
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Blank Chapter
                                </Button>
                            </div>
                        }
                    />
                )}
                <ChaptersTable
                    chapters={filteredChapters}
                    isLoading={isLoading}
                    storyId={storyId}
                    projectId={projectId}
                />
            </div>
        </UnifiedListLayout>
    );
}
